import { Button } from '@kalos/ui';
import { CopyIcon, DownloadIcon, PauseIcon, PlayIcon } from '@radix-ui/react-icons';
import { MicIcon } from 'lucide-react';
import { type FC, type ReactNode } from 'react';

import { formatDurationFromSeconds } from '../../../../tools/formatTimeUtils';
import { PLAYBACK_RATES, useAudioNotePlayback } from './useAudioNotePlayback';

interface AudioNotePlayerProps {
  /** Playable source — a data URL from the gallery's file conversion. */
  src: string;
  mimeType: string;
  /** Name used for the download, not shown in the UI. */
  fileName: string;
  /** Enables the copy control; the transcript body itself comes in as children. */
  transcript?: string;
  onCopyTranscript?: () => void;
  /** Transcript / translation controls rendered under the player. */
  children?: ReactNode;
}

export const AudioNotePlayer: FC<AudioNotePlayerProps> = ({
  src,
  mimeType,
  fileName,
  transcript,
  onCopyTranscript,
  children,
}) => {
  const {
    audioRef,
    isPlaying,
    duration,
    rateIndex,
    togglePlay,
    cycleRate,
    seekTo,
    progress,
    remaining,
  } = useAudioNotePlayback(src);

  const seek = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    seekTo(((event.clientX - rect.left) / rect.width) * duration);
  };

  const copyButton = onCopyTranscript ? (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="h-8 w-8 shrink-0 text-gray-500"
      onClick={onCopyTranscript}
      disabled={!transcript}
      title="Copy transcript"
      aria-label="Copy transcript"
    >
      <CopyIcon className="h-4 w-4" />
    </Button>
  ) : null;

  return (
    <div className="space-y-3">
      <audio ref={audioRef} className="hidden" preload="metadata">
        <source src={src} type={mimeType === 'audio/pcm' ? 'audio/wav' : mimeType} />
      </audio>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MicIcon className="h-4 w-4 shrink-0 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Audio note</span>
          </div>
          {/* Mobile keeps copy on the label row; desktop puts it beside the player */}
          <span className="sm:hidden">{copyButton}</span>
        </div>

        <div className="flex min-w-0 items-center gap-1">
          <div className="border-input flex min-w-0 flex-1 items-center gap-3 rounded-full border bg-white px-3 py-2">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={togglePlay}
              title={isPlaying ? 'Pause' : 'Play'}
              aria-label={isPlaying ? 'Pause audio note' : 'Play audio note'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </Button>

            <div
              className="bg-primary/20 relative h-1.5 min-w-[3rem] flex-1 cursor-pointer rounded-full"
              onClick={seek}
              role="presentation"
            >
              <div
                className="bg-primary absolute left-0 top-0 h-full rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>

            <span className="shrink-0 text-xs font-medium tabular-nums text-gray-600">
              {formatDurationFromSeconds(Math.round(isPlaying ? remaining : duration))}
            </span>

            <button
              type="button"
              onClick={cycleRate}
              className="border-input hover:bg-primary/5 shrink-0 rounded-full border bg-white px-2 py-0.5 text-xs font-medium text-gray-700"
              title="Playback speed"
            >
              {PLAYBACK_RATES[rateIndex]}x
            </button>

            <a
              href={src}
              download={fileName}
              className="shrink-0 text-gray-500 hover:text-gray-700"
              title="Download audio note"
              aria-label="Download audio note"
            >
              <DownloadIcon className="h-4 w-4" />
            </a>
          </div>
          <span className="hidden sm:inline-flex">{copyButton}</span>
        </div>
      </div>

      {children}
    </div>
  );
};
